import React, { useMemo } from 'react';
import { toast } from 'sonner';
import ContractTemplateWYSIWYGEditor from '../../../components/contract-templates/ContractTemplateWYSIWYGEditor';
import FormLabel from '../../../components/forms/FormLabel';
import HorizontalFormBlockWrapper from '../../../components/forms/HorizontalFormBlockWrapper';
import RequiredInput from '../../../components/forms/InputHOC';
import PageHeader from '../../../layout/shared/page-header';
import { menuRoutes } from '../../../router/menu';
import { TutorService } from '../../../services/tutor/tutorService';
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from '../../../utils/editorConversions';
import FormFooter from '../../_layout/_footers/form-footer';
import { useSettingsProvider } from '../provider/SettingsProvider';

const pageHeader = {
    title: 'Ajustes en Tutores',
    breadcrumb: [
        { name: 'Inicio', href: menuRoutes.dashboard.path },
        { name: 'Configuración' },
        { name: 'Tutores', href: menuRoutes.settings.path + menuRoutes.settings.tutors },
    ]
}

const TutorSettingsPage: React.FC = () => {

    const { formik, isLoading, companyConfig, hasCompanyConfigChanges, updateCompanyConfig, setShowModal, discardCompanyConfigChanges } = useSettingsProvider();

    const [variables, setVariables] = React.useState<any>([]);

    // Memoize initialData to avoid editor re-renders. Handles both JSON and HTML of the contractTemplate
    const memoizedInitialData = useMemo(() => {
        if (!companyConfig?.tutorAgreementDocumentTemplate) return undefined;

        try {
            // Try to parse as JSON first (legacy format)
            return JSON.parse(companyConfig.tutorAgreementDocumentTemplate);
        } catch {
            // If it fails, assume it's HTML and convert to JSON
            if (isValidHTML(companyConfig.tutorAgreementDocumentTemplate)) {
                return convertHTMLToEditorJSON(companyConfig.tutorAgreementDocumentTemplate);
            }
            return undefined;
        }
    }, [companyConfig?.tutorAgreementDocumentTemplate]);

    const loadTemplates = async () => {
        try {
            const response = await (new TutorService).getAgreementFields();
            const data = response.getResponseData()?.data;

            const formattedVariables = [
                {
                    type: 'tutor',
                    label: 'Datos del Tutor',
                    variables: Object.entries(data).map(([key, label]) => ({
                        key: `{{${key}}}`,
                        label: label as string
                    }))
                }
            ];

            setVariables(formattedVariables);
        } catch (error) {
            console.error('Error al cargar las variables:', error);
            toast.error('Error al cargar las variables');
        }
    };

    React.useEffect(() => {
        loadTemplates();
    }, []);

    return (
        <div className='h-full overflow-auto scrollbar '>
            <PageHeader
                title={pageHeader.title}
                breadcrumb={pageHeader.breadcrumb}
                className='border-b-4 border-dotted border-grey-500 top-0'
            />
            <HorizontalFormBlockWrapper
                title="Configuración de Tutores"
                description="Sección de configuración dedicada a los tutores registrados en el sistema."
                childrenWrapperClassName="@md:col-span-10 md:grid-cols-2"
            >
                <RequiredInput
                    required
                    id="invoiceName"
                    label="Máximo de tutorias en horario de mañana"
                    value={companyConfig?.tutorMaxMorningTutorials}
                    type={"number"}
                    defaultValue={companyConfig?.tutorMaxMorningTutorials ?? 1}
                    onChange={(e) => {
                        updateCompanyConfig('tutorMaxMorningTutorials', e.target.value);
                        formik.setFieldValue('tutorMaxMorningTutorials', e.target.value);
                    }}
                    error={formik.errors.tutorMaxMorningTutorials}
                    className='pb-4'
                />

                <RequiredInput
                    required
                    id="invoiceName"
                    label="Máximo de tutorias en horario de tarde"
                    value={companyConfig?.tutorMaxAfternoonTutorials}
                    type={"number"}
                    defaultValue={companyConfig?.tutorMaxMorningTutorials ?? 1}
                    onChange={(e) => {
                        updateCompanyConfig('tutorMaxAfternoonTutorials', e.target.value);
                        formik.setFieldValue('tutorMaxAfternoonTutorials', e.target.value);
                    }}
                    error={formik.errors.tutorMaxAfternoonTutorials}
                    className='pb-4'
                />

                {/* Template */}
                <div className="col-span-full">
                    <FormLabel label="Plantilla de Convenio de Tutor" required />
                    <ContractTemplateWYSIWYGEditor
                        type='contract'
                        initialData={memoizedInitialData}
                        onChange={(data) => { formik.setFieldValue('tutorAgreementDocumentTemplate', convertEditorJSONToHTML(data)) }}
                        placeholder="Maqueta aquí la plantilla de convenio de tutor..."
                        variables={variables}
                    />
                    {formik.errors.tutorAgreementDocumentTemplate && (
                        <p className="text-red-500 text-sm mt-1">
                            {formik.errors.tutorAgreementDocumentTemplate}
                        </p>
                    )}
                </div>
            </HorizontalFormBlockWrapper>
            {(hasCompanyConfigChanges || (companyConfig?.tutorAgreementDocumentTemplate !== formik.values.tutorAgreementDocumentTemplate)) && (
                <FormFooter
                    customClassName={"w-full"}
                    submitBtnText={"Aplicar Cambios"}
                    cancelBtnText={"Descartar Cambios"}
                    handleCancelBtn={() => discardCompanyConfigChanges()}
                    handleSubmitBtn={() => setShowModal(true)}
                    isLoading={isLoading}
                    isDisabled={Object.keys(formik.errors).length > 0}
                />

            )}
        </div>
    );
};

export default TutorSettingsPage;
